import type { ProminentSearchOption } from '@videoblocks/design-system';
import { Image } from '@videoblocks/react-icons';

import { type FailureReason } from './failureReason';
import {
    SUPPORTED_IMAGE_SEARCH_MEDIA_TYPES,
    SUPPORTED_VIDEO_SEARCH_MEDIA_TYPES,
    type SupportedVideoSearchMediaType,
} from './types';

export type SelectOption = {
    value: string;
    label: string;
};

/** Fallback when an item reports no ratio, and the ratio at or below which it counts as vertical. */
export const DEFAULT_VIDEO_ASPECT_RATIO = 1.77777;
export const VERTICAL_ASPECT_RATIO_THRESHOLD = 1;

export const VIDEO_GENERATION_MODEL_OPTIONS = [
    {
        value: 'veo3.1_fast',
        version: 'V3.1',
        name: 'Veo 3.1 Fast',
        description: 'Cinematic realism & fluid motion',
    },
] as const;

export const VIDEO_GENERATION_RATIO_OPTIONS: SelectOption[] = [
    {
        value: '16:9',
        label: '16:9',
    },
    {
        value: '9:16',
        label: '9:16',
    },
];

export const VIDEO_GENERATION_DURATION_OPTIONS: SelectOption[] = [
    {
        value: '4',
        label: '4s',
    },
    {
        value: '6',
        label: '6s',
    },
    {
        value: '8',
        label: '8s',
    },
];

// NOTE: The resolution is dependent upon the ratio.
// For example, 16:9 ratio will have a resolution of 1280:720p, and 9:16 ratio will have a resolution of 720:1280p.
// Will need custom logic before the API call to map these correctly.
export const VIDEO_GENERATION_RESOLUTION_OPTIONS: SelectOption[] = [
    {
        value: '720p',
        label: '720p',
    },
    {
        value: '1080p',
        label: '1080p',
    },
];

export const VIDEO_EDIT_MODEL_OPTIONS = [
    {
        value: 'aleph2',
        version: '2.0',
        name: 'Aleph 2.0',
        description: 'Cinematic, high-fidelity videos, realistic motion',
    },
] as const;

export const IMAGE_GENERATION_MODEL_OPTIONS = [
    {
        value: 'gen4_image',
        version: 'V4',
        name: 'Gen4 Image',
        description: 'High-quality photorealistic & stylized images',
    },
] as const;

export const IMAGE_GENERATION_RATIO_OPTIONS: SelectOption[] = [
    { value: '1:1', label: '1:1' },
    { value: '4:3', label: '4:3' },
    { value: '3:4', label: '3:4' },
    { value: '16:9', label: '16:9' },
    { value: '9:16', label: '9:16' },
];

export const IMAGE_GENERATION_RESOLUTION_OPTIONS: SelectOption[] = [
    { value: '1080p', label: '1080p' },
    { value: '720p', label: '720p' },
];

export const IMAGE_GENERATION_RATIO_MAP: Record<string, Record<string, string>> = {
    '1:1': { '720p': '720:720', '1080p': '1080:1080' },
    '4:3': { '720p': '960:720', '1080p': '1440:1080' },
    '3:4': { '720p': '720:960', '1080p': '1080:1440' },
    '16:9': { '720p': '1280:720', '1080p': '1920:1080' },
    '9:16': { '720p': '720:1280', '1080p': '1080:1920' },
};

export const MAX_PROMPT_LENGTH = 1000;
export const VIDEO_GENERATION_MAX_FILE_SIZE_BYTES = 1024 * 1024 * 15; // 15MB
export const IMAGE_GENERATION_MAX_FILE_SIZE_BYTES = 1024 * 1024 * 15; // 15MB

// Stockblocks AiTaskCostCalculator rate for veo3.1_fast (only exposed model). Fast-follow: source from backend.
export const VIDEO_GENERATION_CREDIT_COST_PER_SECOND = 100;

export const getVideoGenerationCreditCost = (duration: number): number => {
    return duration * VIDEO_GENERATION_CREDIT_COST_PER_SECOND;
};

export const IMAGE_GENERATION_CREDIT_COST_BY_RESOLUTION: Record<string, number> = {
    '720p': 50,
    '1080p': 80,
};

export const getImageGenerationCreditCost = (resolution: string): number => {
    return IMAGE_GENERATION_CREDIT_COST_BY_RESOLUTION[resolution] ?? 0;
};

export const VIDEO_EDIT_CREDIT_COST = 750;

export const IMAGE_SEARCH_MEDIA_TYPE_OPTIONS: ProminentSearchOption[] = [
    {
        value: 'Images',
        label: 'Images',
        selectItemLabel: 'All Images',
        icon: <Image width={16} height={16} />,
    },
    ...SUPPORTED_IMAGE_SEARCH_MEDIA_TYPES.map((type) => ({ value: type, label: type })),
];

const VIDEO_SEARCH_MEDIA_TYPE_LABELS: Record<SupportedVideoSearchMediaType, string> = {
    footage: 'Footage',
};

export const VIDEO_SEARCH_MEDIA_TYPE_OPTIONS: ProminentSearchOption[] = SUPPORTED_VIDEO_SEARCH_MEDIA_TYPES.map(
    (type) => ({ value: type, label: VIDEO_SEARCH_MEDIA_TYPE_LABELS[type] }),
);

// User-facing copy for AI generation failure reasons — UI layer only. The pure classification
// (which reason, is it retryable, is it user-fixable) lives in `./failureReason` and is shared
// with the server routes; this copy should be imported only by UI components.
export interface FailureReasonContent {
    title: string;
    subtitle: string;
}

const FAILURE_REASON_CONTENT: Record<FailureReason, FailureReasonContent> = {
    moderation: {
        title: 'Content not allowed',
        subtitle:
            'Sorry, your generation was blocked by our content moderation filters. Try again with a different prompt.',
    },
    aspect_ratio: {
        title: 'Image aspect ratio out of range',
        subtitle:
            "Your reference image's aspect ratio is out of range. The longer side of your image should be twice the size of the shorter side at most.",
    },
    // TODO: Discriminate between image and video.
    asset_too_large: {
        title: 'Image too large',
        subtitle: 'Your reference image is too large - try again with an image under 16MB.',
    },
    provider_error: {
        title: 'Something went wrong',
        subtitle: 'Something went wrong. Please try again.',
    },
    generation_failed: {
        title: 'Generation failed',
        subtitle: 'Generation failed. Please try again.',
    },
    timed_out: {
        title: 'Generation timed out',
        subtitle: 'This generation took too long to complete. Your credits have been returned.',
    },
};

export function failureReasonContent(reason: FailureReason): FailureReasonContent {
    return FAILURE_REASON_CONTENT[reason];
}

// User-facing copy explaining why an AI Toolkit input is disabled — UI layer only.
export const DISABLED_INPUT_REASONS = {
    singleModel: 'More models coming soon',
    durationLocked: 'Duration must be 8 seconds when resolution is 1080p',
} as const;

export const ASPECT_RATIO_VALIDATION_MESSAGE =
    'Image is too wide or tall. The longer side should be at most twice the shorter side.';

export const MAX_DIMENSION_VALIDATION_MESSAGE = 'Image is too large. Width and height must each be 8,000 px or less.';
